import React, { SFC, useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UserModel, UserUnpaidSummary } from '../../../shared/types/user'
import {
    Grid,
    Paper,
    IconButton,
    Button,
    Tabs,
    Typography,
    Tab,
    CircularProgress,
    Card,
    CardContent,
    ListItemIcon,
    Menu,
    MenuItem,
    ListItemText,
    Checkbox,
    TextField,
    Snackbar,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import Alert, { Color } from '@material-ui/lab/Alert'
import { ConfirmationDialog } from '../../../shared/components/confirmationDialog'
import ValidationError from '../../../shared/utils/validationError'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import QueueIcon from '@material-ui/icons/Queue'
import DriveEtaIcon from '@material-ui/icons/DriveEta'
import CloseIcon from '@material-ui/icons/Close'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'
import { TabPanel } from '../../../shared/components/tabPanel'
import SessionCard from '../../history/SessionCard'
import { fetchSessionsForUser, resetSessionsForUser } from '../../../redux/actions/users'
import { fetchLocations, fetchLocationForDetail, setSelectedLocationId } from '../../../redux/actions/locations'
import { getJson, sendJson } from '../../../shared/utils/api'
import { LocationInfo } from '../../../shared/types/location'
import { ChargingSession, ChargingSessionState, PaymentStatus, PaymentInterval, ChargingInitSources } from '../../../shared/types/chargingSession'
import moment, { Moment } from 'moment'
import { Image } from '../../../shared/components/Image'
import { NoData } from '../../../shared/components/NoData'
import { config } from '../../../config'
import UserAddEditDialog from '../userAddEditDialog/UserAddEditDialog'
import { ChargerConnectorSocketType, getChargerConnectorSocketTypes } from '../../../shared/types/charger'
import { TagModel } from '../../../shared/types/tag'
import { TenantModel } from '../../../shared/types/tenant'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

const useStyles = makeStyles((theme) => ({
    cardWrapper: {
        height: '100%',
        width: `calc(100% - ${theme.spacing(1)})`,
        paddingLeft: theme.spacing(1),
        '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    cardWrapperInner: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
    },
    tabContentWrapper: {
        flexGrow: 1,
        overflowY: 'auto',
        height: '100%',
        padding: theme.spacing(3),
    },
    tabContent: {
        height: '100%',
    },
    card: {
        height: '100%',
    },
    titleWrapper: {
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        flex: '0',
    },
    titleText: {
        textAlign: 'left',
        minWidth: 0,
    },
    titleButtons: {
        textAlign: 'right',
    },
    profileItem: {
        textAlign: 'left',
    },
    profileValue: {
        paddingLeft: theme.spacing(1),
        textAlign: 'left',
        position: 'relative',
        width: '80%',
        '& span': {
            position: 'absolute',
            top: theme.spacing(0.3),
        },
    },
    groupDivider: {
        height: theme.spacing(1.5),
    },
    sessionDayTitle: {
        marginLeft: theme.spacing(1),
    },
    sessionDayTitleDivider: {
        marginTop: theme.spacing(3),
    },
    sessionLoadMore: {
        marginBottom: theme.spacing(3),
    },
    fullHeight: {
        height: '100%',
    },
    dropdownIcon: {
        minWidth: '32px',
    }
}))

interface UserCardProps {
    user: UserModel
    onDelete: Function
    onEdit: Function
    onEditCars: Function
    // zavola sa po zmene pouzivatela priamo z karty (vymozenie platby, odstranenie karty) - rodic obnovi zoznam
    onUserChanged?: Function
}

// admin akcie nad platbami pouzivatela
//  collect     = uhradit vsetky nedoplatky (priebezny mesiac s mesacnou fakturaciou sa nevyuctuje - nie je to dlh)
//  session     = uhradit jednu konkretnu session (len ak je nedoplatkom)
//  removeCard  = odstranit ulozenu platobnu kartu
type PaymentAction =
    | { type: 'collect' }
    | { type: 'session'; session: ChargingSession }
    | { type: 'removeCard' }

const UserCard: SFC<UserCardProps> = ({ user, onDelete, onEdit, onEditCars, onUserChanged }) => {
    const classes = useStyles()
    const { t } = useTranslation()

    const dispatch = useDispatch()
    const history = useHistory()
    const sessionsState = useSelector((state: any) => state.users.sessions)
    const [unpaidSummary, setUnpaidSummary] = useState<UserUnpaidSummary | null>(null)
    const [onlyUnpaid, setOnlyUnpaid] = useState<boolean>(false)
    const allTags = useSelector((state: any) => state.tags.items.user.value)
    const allTenants = useSelector((state: any) => state.tenants.value)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [paymentAction, setPaymentAction] = useState<PaymentAction | null>(null)
    const [paymentActionInProgress, setPaymentActionInProgress] = useState<boolean>(false)
    const [paymentAlert, setPaymentAlert] = useState<{ message: string; severity: Color } | null>(null)
    const hasPaymentCard = Boolean(user.paymentRequestId)
    const loggedTenantId = useSelector((state: any) => state.login.value.user.tenantId)
    // uhrada nedoplatkov a odstranenie karty - len pre admina hlavneho tenanta (superadmin); backend to kontroluje tiez
    const isMainTenantAdmin = Boolean(loggedTenantId) && loggedTenantId === config.mainTenantId

    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)
    const allLocations = useSelector((state: any) => state.locations.items.value) ?? []
    const [whitelistedLocations, setWhitelistedLocations] = useState<LocationInfo[]>([])
    const [whitelistLoading, setWhitelistLoading] = useState<boolean>(false)
    const [whitelistMutating, setWhitelistMutating] = useState<boolean>(false)
    const [addLocationValue, setAddLocationValue] = useState<LocationInfo | null>(null)

    const loadRfids = () => {
        let rfidString = ''
        user.rfids?.forEach((rfidObj, index) => {
            rfidString += rfidObj.rfid
            if (user.rfids && index < user.rfids.length - 1) {
                rfidString += ', '
            }
        })
        return rfidString
    }

    const loadTags = () => {
        if (user.tags?.length && allTags.length) {
            const tags = allTags.filter((tagObj: TagModel) => user.tags?.some((userTag) => userTag.tagId === tagObj.id))
            return tags.map((tagObj: TagModel) => tagObj.name).join(', ')
        }
    }

    const loadTenants = () => {
        if (allTenants != null && user.tenants) {
            return user.tenants
                .split(',')
                .map((id) => id.trim())
                .map((id) => allTenants.find((t: TenantModel) => t.id === id))
                .filter(Boolean)
                .map((t) => t.name)
                .join(', ')
        }
        return ''
    }

    const socketTypeLabels = getChargerConnectorSocketTypes(t)

    const mapSocketTypes = (types: ChargerConnectorSocketType[]) => {
        return types.map((type) => socketTypeLabels.find((socket) => socket.id === type)?.label || type).join(', ')
    }

    const [tabIndex, setTabIndex] = useState<number>(0)
    const userValues = [
        [t('users.firstName'), user.firstName],
        [t('users.lastName'), user.lastName],
        [t('users.email'), user.email],
        [t('users.phone'), user.phone],
        [],
        [t('users.rfid'), loadRfids()],
        [t('users.tags'), loadTags()],
        [t('users.tenants'), loadTenants()],
        [],
        [
            t('users.monthlyAllowance'),
            (user.monthlyAllowance ?? 0) > 0
                ? `${(user.monthlySpent ?? 0)} / ${user.monthlyAllowance} €`
                : '0 €',
        ],
        [t('users.address')],
        [t('users.street'), user.street],
        [t('users.city'), user.city],
        [t('users.zip'), user.zip],
        [t('users.state'), user.country],
        [t('users.companyInformation')],
        [t('users.companyName'), user.companyName],
        [t('users.companyId'), user.companyId],
        [t('users.taxId'), user.taxId],
        [t('users.vatId'), user.vatId],
        [],
        [t('users.card')],
        [t('users.cardNumber'), user.cardNumber ? `**** **** **** ${user.cardNumber}` : ''],
        [t('users.expirationDate'), user.cardExpiration],
    ]

    const [addEditDialogState, setAddEditDialogState] = useState({
        open: false,
        isAdd: true,
        onClose: (user: UserModel) => { },
        initialModel: undefined as UserModel | undefined,
    })

    const openAddDialog = () => {
        setAddEditDialogState({
            open: true,
            isAdd: true,
            onClose: onAddDialogClose,
            initialModel: user,
        })
    }
    const onAddDialogClose = () => {
        setAddEditDialogState({ ...addEditDialogState, open: false })
    }

    const isSessionActuallyUnpaid = (session: ChargingSession): boolean => {
        if (session.whitelistAuthentication) {
            return false
        }
        if (session.paymentStatus === PaymentStatus.ROAMING) {
            return false
        }
        const price = session.price > 0 ? session.price : (session.chargingPrice ?? 0) + (session.parkingPrice ?? 0)
        if (price <= 0) {
            return false
        }
        // stlpec Paid sa uz nepouziva - rozhoduje len paymentStatus
        return session.paymentStatus === PaymentStatus.UNPAID
    }

    const sessionsWithDate: any[] = []
    let lastStartedAtDay: Moment
    const filteredSessionsData = onlyUnpaid
        ? sessionsState.data.filter((x: ChargingSession) => isSessionActuallyUnpaid(x))
        : sessionsState.data
    filteredSessionsData.forEach((x: ChargingSession) => {
        const currentStartedAtDay = moment(x.startedAt).startOf('day')
        if (!currentStartedAtDay.isSame(lastStartedAtDay)) {
            sessionsWithDate.push({ date: currentStartedAtDay.format('LL'), value: null })
            lastStartedAtDay = currentStartedAtDay
        }

        sessionsWithDate.push({ date: null, value: x })
    })

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const sessionsKey = onlyUnpaid ? `${user.id}:unpaid` : user.id

    useEffect(() => {
        setOnlyUnpaid(false)
    }, [user.id])

    useEffect(() => {
        if (tabIndex === 1 && sessionsState.executed && sessionsState.key !== sessionsKey) {
            dispatch(fetchSessionsForUser(user, sessionsState, onlyUnpaid))
        }
    }, [dispatch, tabIndex, user, sessionsState, onlyUnpaid, sessionsKey])

    const reloadUnpaidSummary = () => {
        getJson(`users/${user.id}/unpaidSummary`)
            .then((result: UserUnpaidSummary) => setUnpaidSummary(result))
            .catch(() => setUnpaidSummary(null))
    }

    const getPaymentActionTexts = (action: PaymentAction) => {
        const card = user.cardNumber ?? ''
        switch (action.type) {
            case 'collect':
                return {
                    title: t('users.collectDebtTitle'),
                    content: t('users.collectDebtContent', { card, amount: (unpaidSummary?.instantUnpaid ?? 0).toFixed(2) }),
                    confirm: t('users.collectDebt'),
                }
            case 'session': {
                const s = action.session
                const price = s.price > 0 ? s.price : (s.chargingPrice ?? 0) + (s.parkingPrice ?? 0)
                return {
                    title: t('users.paySessionTitle'),
                    content: t('users.paySessionContent', {
                        card,
                        amount: price.toFixed(2),
                        date: moment(s.startedAt).format('LLL'),
                        location: s.location?.name ?? '',
                    }),
                    confirm: t('users.paySessionConfirm'),
                }
            }
            case 'removeCard':
                return {
                    title: t('users.removeCardTitle'),
                    content: t('users.removeCardContent', { card }),
                    confirm: t('users.removeCard'),
                }
        }
    }

    const getPaymentErrorMessage = (error: any): string => {
        const code = error instanceof ValidationError ? error.errors?.[0]?.code : undefined
        // kody z backendu (DebtPaymentResultType): 1 = platba zlyhala, 2 = bez karty, 3 = prilis casto
        // eslint-disable-next-line eqeqeq
        if (code == 1) return t('users.collectDebtFailed')
        // eslint-disable-next-line eqeqeq
        if (code == 2) return t('users.noPaymentCard')
        // eslint-disable-next-line eqeqeq
        if (code == 3) return t('users.collectDebtTooOften')
        // eslint-disable-next-line eqeqeq
        if (code == 4) return t('users.sessionNotInDebt')
        return t('users.paymentActionError')
    }

    // je session nedoplatok, ktory moze admin uhradit?
    // primarne podla zoznamu z backendu (unpaidSummary.debtSessionIds = spolocna definicia DebtSql);
    // ak ho backend nevrati (starsia verzia), rovnaka definicia sa vyhodnoti tu
    const debtSessionIdSet = new Set((unpaidSummary?.debtSessionIds ?? []).map((id) => id.toLowerCase()))
    const isDebtSession = (session: ChargingSession): boolean => {
        if (unpaidSummary?.debtSessionIds) {
            return debtSessionIdSet.has(String(session.id).toLowerCase())
        }
        if (session.state !== ChargingSessionState.Finished) return false
        if (session.chargingInitSource !== ChargingInitSources.SEAK) return false
        if (!isSessionActuallyUnpaid(session)) return false
        const paymentError = (session.errorCause ?? '').startsWith('paymentError')
        if (session.paymentInterval === PaymentInterval.INSTANT) {
            const graceOver = session.endedAt != null && moment().diff(moment(session.endedAt), 'minutes') >= 15
            return paymentError || graceOver
        }
        if (session.paymentInterval === PaymentInterval.MONTHLY) {
            return paymentError || moment(session.startedAt).isBefore(moment().startOf('month'))
        }
        return false
    }

    const onPaySession = useCallback((session: ChargingSession) => setPaymentAction({ type: 'session', session }), [])

    const runPaymentAction = async () => {
        if (!paymentAction) return
        const action = paymentAction
        setPaymentActionInProgress(true)
        try {
            if (action.type === 'removeCard') {
                await sendJson(`users/${user.id}/paymentCard`, 'DELETE')
                setPaymentAlert({ message: t('users.removeCardSuccess'), severity: 'success' })
            } else {
                const url =
                    action.type === 'session'
                        ? `users/${user.id}/sessions/${action.session.id}/pay`
                        : `users/${user.id}/payDebts`
                const result: UserUnpaidSummary = await sendJson(url, 'POST')
                const remainingDebt = result?.instantUnpaid ?? 0
                setPaymentAlert({
                    message:
                        remainingDebt > 0
                            ? t('users.collectDebtSuccessRemaining', { amount: remainingDebt.toFixed(2) })
                            : t('users.collectDebtSuccess'),
                    severity: 'success',
                })
            }
        } catch (error) {
            setPaymentAlert({ message: getPaymentErrorMessage(error), severity: 'error' })
        } finally {
            setPaymentActionInProgress(false)
            setPaymentAction(null)
            // platba mohla zmenit stav sessions aj pri chybe (napr. ciastocna uhrada) - obnovime vsetko
            reloadUnpaidSummary()
            dispatch(resetSessionsForUser())
            if (onUserChanged) onUserChanged()
        }
    }

    useEffect(() => {
        if (tabIndex === 1) {
            let cancelled = false
            getJson(`users/${user.id}/unpaidSummary`)
                .then((result: UserUnpaidSummary) => {
                    if (!cancelled) {
                        setUnpaidSummary(result)
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setUnpaidSummary(null)
                    }
                })
            return () => {
                cancelled = true
            }
        }
    }, [tabIndex, user.id])

    useEffect(() => {
        if (tabIndex === 3) {
            let cancelled = false
            setWhitelistLoading(true)
            getJson(`users/${user.id}/whitelistedLocations`)
                .then((result: LocationInfo[]) => {
                    if (!cancelled) {
                        setWhitelistedLocations(result ?? [])
                        setWhitelistLoading(false)
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setWhitelistedLocations([])
                        setWhitelistLoading(false)
                    }
                })
            return () => {
                cancelled = true
            }
        }
    }, [tabIndex, user.id])

    useEffect(() => {
        if (tabIndex === 3 && allLocations.length === 0) {
            dispatch(fetchLocations(tenantId))
        }
    }, [dispatch, tabIndex, tenantId, allLocations.length])

    const refreshLocationWhitelistCaches = (locationId: string) => {
        dispatch(fetchLocationForDetail(locationId))
        dispatch(fetchLocations(tenantId))
    }

    const handleAddToWhitelist = () => {
        if (!addLocationValue) return
        const locationToAdd = addLocationValue
        setWhitelistMutating(true)
        sendJson(`locations/${locationToAdd.id}/whitelist/${user.id}`, 'POST')
            .then(() => {
                setWhitelistedLocations((prev) => [...prev, locationToAdd])
                setAddLocationValue(null)
                setWhitelistMutating(false)
                refreshLocationWhitelistCaches(locationToAdd.id)
            })
            .catch(() => {
                setWhitelistMutating(false)
            })
    }

    const handleRemoveFromWhitelist = (locationId: string) => {
        setWhitelistMutating(true)
        sendJson(`locations/${locationId}/whitelist/${user.id}`, 'DELETE')
            .then(() => {
                setWhitelistedLocations((prev) => prev.filter((loc) => loc.id !== locationId))
                setWhitelistMutating(false)
                refreshLocationWhitelistCaches(locationId)
            })
            .catch(() => {
                setWhitelistMutating(false)
            })
    }

    const availableLocationsToAdd = allLocations.filter(
        (loc: LocationInfo) => !whitelistedLocations.some((wl) => wl.id === loc.id)
    )

    const goToLocation = (locationId: string) => {
        dispatch(setSelectedLocationId(locationId))
        history.push('/locations')
    }

    return (
        <div className={classes.cardWrapper}>
            <Paper elevation={1} className={classes.card}>
                <div className={classes.cardWrapperInner}>
                    <Grid item xs={12} container className={classes.titleWrapper}>
                        <Grid item xs={10} className={classes.titleText}>
                            <Typography variant="h2" noWrap style={{ padding: 0 }}>
                                {user.firstName} {user.lastName}
                            </Typography>
                        </Grid>
                        <Grid className={classes.titleButtons}>
                            <Button
                                onClick={handleMenuOpen}
                                endIcon={<ExpandMoreIcon />}
                            >
                                Actions
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem
                                    onClick={() => {
                                        openAddDialog();
                                        handleMenuClose();
                                    }}
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><QueueIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Duplicate user" />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        onEdit();
                                        handleMenuClose();
                                    }}
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><EditIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Edit User" />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        onEditCars();
                                        handleMenuClose();
                                    }}
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><DriveEtaIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Manage cars" />
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        onDelete();
                                        handleMenuClose();
                                    }}
                                    style={{ color: 'red' }} // zvýraznenie Delete
                                >
                                    <ListItemIcon className={classes.dropdownIcon}><DeleteIcon fontSize="small" style={{ color: 'red' }} /></ListItemIcon>
                                    <ListItemText primary="Remove user" />
                                </MenuItem>
                            </Menu>
                            {addEditDialogState.open && <UserAddEditDialog {...addEditDialogState} />}
                            {paymentAction && (
                                <ConfirmationDialog
                                    open={Boolean(paymentAction)}
                                    title={getPaymentActionTexts(paymentAction).title}
                                    content={getPaymentActionTexts(paymentAction).content}
                                    confirmLabel={getPaymentActionTexts(paymentAction).confirm}
                                    cancelLabel={t('common.cancel')}
                                    inProgress={paymentActionInProgress}
                                    onConfirm={() => {
                                        if (!paymentActionInProgress) runPaymentAction()
                                    }}
                                    onCancel={() => {
                                        if (!paymentActionInProgress) setPaymentAction(null)
                                    }}
                                />
                            )}
                            {paymentAlert && (
                                <Snackbar
                                    open
                                    autoHideDuration={paymentAlert.severity === 'success' ? 5000 : null}
                                    onClose={() => setPaymentAlert(null)}
                                >
                                    <Alert onClose={() => setPaymentAlert(null)} severity={paymentAlert.severity}>
                                        {paymentAlert.message}
                                    </Alert>
                                </Snackbar>
                            )}
                        </Grid>
                    </Grid>
                    <div>
                        <Tabs value={tabIndex} onChange={(event: any, newValue: number) => setTabIndex(newValue)}>
                            <Tab label={t('users.profile')} color="primary" />
                            <Tab label={t('users.history')} />
                            <Tab label={t('users.cars')} />
                            <Tab label={t('users.whitelist')} />
                        </Tabs>
                    </div>
                    <div className={classes.tabContentWrapper}>
                        <TabPanel value={tabIndex} index={0} className={classes.tabContent}>
                            <Grid container className={classes.fullHeight}>
                                {userValues.map((x: any, index) =>
                                    x.length === 2 ? (
                                        <React.Fragment key={index}>
                                            <Grid item sm={3} className={classes.profileItem}>
                                                <Typography variant="subtitle2">{x[0]}</Typography>
                                            </Grid>
                                            <Grid item sm={9} className={classes.profileValue}>
                                                <Typography variant="caption" component="div">
                                                    {x[1] && x[1] !== '' ? x[1] : '\u00A0'}
                                                </Typography>
                                            </Grid>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment key={index}>
                                            <Grid item sm={12} className={classes.groupDivider} />
                                            {x.length === 1 && (
                                                <Grid item sm={12} className={classes.profileItem} style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="h6">{x[0]}</Typography>
                                                    {/* odstranenie karty - len ak ma user ulozenu kartu */}
                                                    {isMainTenantAdmin && x[0] === t('users.card') && (hasPaymentCard || Boolean(user.cardNumber)) && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            style={{ color: '#c62828', borderColor: '#c62828', marginLeft: '16px' }}
                                                            disabled={paymentActionInProgress}
                                                            onClick={() => setPaymentAction({ type: 'removeCard' })}
                                                        >
                                                            {t('users.removeCard')}
                                                        </Button>
                                                    )}
                                                </Grid>
                                            )}
                                        </React.Fragment>
                                    )
                                )}
                            </Grid>
                        </TabPanel>

                        <TabPanel value={tabIndex} index={1} className={classes.tabContent}>
                            {unpaidSummary && unpaidSummary.totalUnpaid > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                                <Card elevation={1} style={{ flex: 1, padding: '12px 16px', backgroundColor: '#fdecea', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <Typography variant="subtitle2" style={{ color: '#c62828' }}>
                                            {t('users.unpaidTotal', { amount: unpaidSummary.totalUnpaid.toFixed(2) })}
                                        </Typography>
                                        {unpaidSummary.currentMonthUnpaid > 0 && (
                                            <Typography variant="caption" style={{ color: '#c62828' }}>
                                                {t('users.unpaidCurrentMonth', { amount: unpaidSummary.currentMonthUnpaid.toFixed(2) })}
                                            </Typography>
                                        )}
                                    </div>
                                    <Typography variant="caption" style={{ color: '#000000', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                        <Checkbox
                                            checked={onlyUnpaid}
                                            disabled={!sessionsState.executed}
                                            onChange={() => setOnlyUnpaid(!onlyUnpaid)}
                                            style={{ color: '#6100ED', padding: '4px' }}
                                        />
                                        {t('users.showOnlyUnpaid')}
                                        {!sessionsState.executed && (
                                            <CircularProgress size={16} style={{ marginLeft: '8px' }} />
                                        )}
                                    </Typography>
                                </Card>
                                {/* uhrada nedoplatkov - len ak ma user kartu a nedoplatok (priebezny mesiac s mesacnou fakturaciou nie je nedoplatok) */}
                                {isMainTenantAdmin && hasPaymentCard && unpaidSummary.instantUnpaid > 0 && (
                                    <Button
                                        variant="outlined"
                                        style={{ color: '#c62828', borderColor: '#c62828', whiteSpace: 'nowrap' }}
                                        disabled={paymentActionInProgress}
                                        onClick={() => setPaymentAction({ type: 'collect' })}
                                    >
                                        {t('users.collectDebtAmount', { amount: unpaidSummary.instantUnpaid.toFixed(2) })}
                                    </Button>
                                )}
                                </div>
                            )}
                            {sessionsWithDate.map((sessionWithDate: any, index) =>
                                sessionWithDate.date ? (
                                    <Typography
                                        variant="subtitle2"
                                        key={sessionWithDate.date}
                                        className={`${classes.sessionDayTitle} ${index > 0 ? classes.sessionDayTitleDivider : ''
                                            }`}
                                    >
                                        {sessionWithDate.date}
                                    </Typography>
                                ) : (
                                    <SessionCard
                                        key={sessionWithDate.value.id}
                                        session={sessionWithDate.value}
                                        canPay={isMainTenantAdmin && hasPaymentCard && isDebtSession(sessionWithDate.value)}
                                        payDisabled={paymentActionInProgress}
                                        onPay={onPaySession}
                                    />
                                )
                            )}

                            {sessionsState.hasMorePages && sessionsState.executed && (
                                <Button
                                    className={classes.sessionLoadMore}
                                    variant="contained"
                                    color="primary"
                                    onClick={() => {
                                        dispatch(fetchSessionsForUser(user, sessionsState, onlyUnpaid))
                                    }}
                                >
                                    {t('common.loadMore')}
                                </Button>
                            )}
                            {!sessionsState.executed ? (
                                <CircularProgress />
                            ) : (
                                sessionsWithDate.length === 0 && <NoData text={t('users.noSessions')} />
                            )}
                        </TabPanel>
                        <TabPanel value={tabIndex} index={2} className={classes.tabContent}>
                            <Grid container spacing={2}>
                                {user.cars?.map((car: any, index: number) => (
                                    <Grid item xs={12} md={6} key={index}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    {/* Brand je povinný, model nemusí byť */}
                                                    {`${car.brand} ${car.model ?? ''}`.trim()}
                                                    {user.cars && user.cars.length > 1 && car.default
                                                        ? ` (${t('users.default')})`
                                                        : ''}
                                                </Typography>

                                                {/* EČV - ak nie je, zobrazíme pomlčku alebo text */}
                                                <Typography variant="body2">
                                                    <strong>{t('users.licensePlate')}:</strong>{' '}
                                                    {car.carLicensePlate || '---'}
                                                </Typography>

                                                <Typography variant="body2">
                                                    <strong>{t('users.socketType')}:</strong>{' '}
                                                    {car.socketType?.length > 0
                                                        ? mapSocketTypes(car.socketType)
                                                        : t('users.noSocket')}
                                                </Typography>

                                                <Typography variant="body2">
                                                    <strong>{t('users.batteryCapacity')}:</strong>{' '}
                                                    {car.batteryCapacity ?? '---'} kWh
                                                </Typography>

                                                {/* Dynamické zobrazenie AC/DC len ak existujú a sú > 0 */}
                                                {car.maxAcPower > 0 && (
                                                    <Typography variant="body2">
                                                        <strong>{t('users.maxAcPower')}:</strong> {car.maxAcPower} kW
                                                    </Typography>
                                                )}

                                                {car.maxDcPower > 0 && (
                                                    <Typography variant="body2">
                                                        <strong>{t('users.maxDcPower')}:</strong> {car.maxDcPower} kW
                                                    </Typography>
                                                )}

                                                {car.year && (
                                                    <Typography variant="body2">
                                                        <strong>{t('users.year')}:</strong> {car.year}
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </TabPanel>
                        <TabPanel value={tabIndex} index={3} className={classes.tabContent}>
                            <Grid container spacing={2} alignItems="flex-start" style={{ marginBottom: '16px' }}>
                                <Grid item xs={9}>
                                    <Autocomplete
                                        options={availableLocationsToAdd}
                                        getOptionLabel={(loc: LocationInfo) => loc.name}
                                        value={addLocationValue}
                                        onChange={(event: any, value: LocationInfo | null) => setAddLocationValue(value)}
                                        renderInput={(params) => (
                                            <TextField {...params} variant="filled" label={t('users.selectLocation')} />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={!addLocationValue || whitelistMutating}
                                        onClick={handleAddToWhitelist}
                                        style={{ height: '56px' }}
                                    >
                                        {t('users.addToWhitelist')}
                                    </Button>
                                </Grid>
                            </Grid>

                            {whitelistLoading ? (
                                <CircularProgress />
                            ) : whitelistedLocations.length === 0 ? (
                                <NoData text={t('users.noWhitelistedLocations')} />
                            ) : (
                                whitelistedLocations.map((loc: LocationInfo) => (
                                    <Card key={loc.id} style={{ marginBottom: '8px' }}>
                                        <CardContent
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <div>
                                                <Typography variant="subtitle2">{loc.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {loc.city}, {loc.street} {loc.houseNum}, {loc.postalCode}
                                                </Typography>
                                            </div>
                                            <div>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => goToLocation(loc.id)}
                                                    title={t('users.showLocation')}
                                                >
                                                    <OpenInNewIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    disabled={whitelistMutating}
                                                    onClick={() => handleRemoveFromWhitelist(loc.id)}
                                                    title={t('common.delete')}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </TabPanel>
                    </div>
                </div>
            </Paper>
        </div>
    )
}

export default UserCard
